import { ApplicationState } from '~/store';
import { BatchEntryState } from '~/reducers/batch-entry';
import { createSelector } from 'reselect';
import {
  getBatchEntryIdFromProps,
  getBatchEntryIdsFromProps,
  getOrderObjectIdFromProps,
  getOrderObjectIdsFromProps,
} from '~/selectors/props.selectors';
import BusinessRule from '~/types/BusinessRule';
import { OrderSelection } from '~/types/Order';
import { BatchEntryExtended, requiresNewRates } from '~/types/OrderBatchEntry';
import { BatchEntryStatus } from '~/types/OrdersBatch';

export const getBatchEntryStore = (state: ApplicationState) => state.batchEntry;

export const getBatchEntryData = createSelector(
  getBatchEntryStore,
  (batchEntryStore) => batchEntryStore.data,
);

export const getBatchEntryAssociatedRules = createSelector(
  getBatchEntryStore,
  (batchEntryStore) => batchEntryStore.associatedRules,
);

export const getBatchEntriesByOrderId = createSelector(
  getBatchEntryStore,
  (batchEntryStore): HashMap<string, number[]> => batchEntryStore.byOrderId,
);

export const getBatchEntriesByOrderObjectIds = createSelector(
  getBatchEntryStore,
  getOrderObjectIdsFromProps,
  (batchEntryStore, orderObjectIds): BatchEntryExtended[] =>
    orderObjectIds.reduce((batchEntries: BatchEntryExtended[], orderObjectId) => {
      const batchEntryIds = batchEntryStore.byOrderId[orderObjectId] || [];
      return batchEntries.concat(
        batchEntryIds.map((id) => batchEntryStore.data[id]).filter((be) => be),
      );
    }, []),
);

export const getBatchEntryLoadingStates = createSelector(
  getBatchEntryStore,
  (batchEntryStore) => batchEntryStore.loadState,
);

export const getBatchEntriesIdByOrderObjectId = createSelector(
  getBatchEntryStore,
  getOrderObjectIdFromProps,
  (batchEntryStore, orderObjectId): number[] => {
    return (
      (batchEntryStore.byOrderId[orderObjectId] &&
        batchEntryStore.byOrderId[orderObjectId]
          .map((beId) => batchEntryStore.data[beId])
          .filter((be) => be)
          .sort((a, b) => a.partialFulfillmentRank - b.partialFulfillmentRank)
          .map((be) => be.id)) ||
      []
    );
  },
);

const getSortedBatchEntriesByBatchEntryIds = (
  batchEntryStore: BatchEntryState,
  batchEntryIds: number[],
) => {
  const batchEntriesWithValidEntries = batchEntryIds
    .map((beId: number) => batchEntryStore.data[beId])
    .filter((be: BatchEntryExtended) => be);

  return batchEntriesWithValidEntries
    .sort((a, b) => a.partialFulfillmentRank - b.partialFulfillmentRank)
    .map((be) => be.id);
};
const getBatchEntryIdsByOrderObjectId = (
  batchEntryStore: BatchEntryState,
  orderObjectId: string,
) => (batchEntryStore && batchEntryStore.byOrderId ? batchEntryStore.byOrderId[orderObjectId] : []);

export const getOrderSelectionForOrderObjectIdsForUseSelectorHook = (orderObjectIds: string[]) =>
  createSelector(getBatchEntryStore, (batchEntryStore): OrderSelection => {
    return orderObjectIds.reduce((acc: Record<string, number[]>, orderObjectId: string) => {
      const batchEntryIds = getBatchEntryIdsByOrderObjectId(batchEntryStore, orderObjectId);
      const sortedBatchEntryIds = getSortedBatchEntriesByBatchEntryIds(
        batchEntryStore,
        batchEntryIds || [],
      );
      acc[orderObjectId] = sortedBatchEntryIds;
      return acc;
    }, {});
  });

export const makeGetBatchEntryById = () => {
  return createSelector(
    getBatchEntryData,
    getBatchEntryIdFromProps,
    (batchEntryData, batchEntryId): Maybe<BatchEntryExtended> =>
      batchEntryData[batchEntryId] || null,
  );
};

export const getBatchEntryById = createSelector(
  getBatchEntryData,
  getBatchEntryIdFromProps,
  (batchEntryData, batchEntryId): Maybe<BatchEntryExtended> => batchEntryData[batchEntryId] || null,
);

export function getBatchEntryByIdsStateless(
  batchEntryData: HashMap<number, BatchEntryExtended>,
  batchEntryIds: number[],
): BatchEntryExtended[] {
  return batchEntryIds
    .map((batchEntryId) => batchEntryData[batchEntryId])
    .filter((batchEntry) => batchEntry);
}

export const getBatchEntriesByIds = createSelector(
  getBatchEntryData,
  getBatchEntryIdsFromProps,
  getBatchEntryByIdsStateless,
);

export const makeGetIsOrderBatchEntryLoading = () => {
  return createSelector(
    getBatchEntryLoadingStates,
    getOrderObjectIdFromProps,
    (loadingStates, orderObjectId) => !!loadingStates[orderObjectId],
  );
};

export function batchEntryIsCompleted(batchEntry?: BatchEntryExtended): boolean {
  return !!batchEntry && batchEntry.objectStatus === BatchEntryStatus.COMPLETE_TRANSACTION;
}

export const makeGetBatchEntryIsCompleted = () => {
  const getBatchEntryById = makeGetBatchEntryById();

  return createSelector(getBatchEntryById, (batchEntry): boolean =>
    batchEntryIsCompleted(batchEntry || undefined),
  );
};

export const getBatchEntryIsCompleted = createSelector(getBatchEntryById, (batchEntry): boolean =>
  batchEntry ? batchEntry.objectStatus === BatchEntryStatus.COMPLETE_TRANSACTION : false,
);

export const makeGetBatchEntryByIds = () => {
  return createSelector(
    getBatchEntryData,
    getBatchEntryIdsFromProps,
    (batchEntryData, batchEntryIds): Maybe<BatchEntryExtended[]> =>
      batchEntryIds.length > 0 ? batchEntryIds?.map((id) => batchEntryData[id] || null) : [],
  );
};

export function batchEntryIsProcessingShipment(batchEntry?: BatchEntryExtended): boolean {
  return (
    !!batchEntry &&
    !batchEntry.shipmentObjectId &&
    batchEntry.objectStatus === BatchEntryStatus.PROCESSING_SHIPMENT
  );
}

export const makeGetIsBatchEntryProcessingShipment = () => {
  const getBatchEntryById = makeGetBatchEntryById();

  return createSelector(getBatchEntryById, (batchEntry): boolean =>
    batchEntry
      ? !batchEntry.shipmentObjectId &&
        batchEntry.objectStatus === BatchEntryStatus.PROCESSING_SHIPMENT
      : false,
  );
};

export const getIsBatchEntryProcessingShipment = createSelector(
  getBatchEntryById,
  (batchEntry): boolean =>
    batchEntry
      ? !batchEntry.shipmentObjectId &&
        batchEntry.objectStatus === BatchEntryStatus.PROCESSING_SHIPMENT
      : false,
);

export const makeGetIsBatchEntriesProcessingShipment = () => {
  const getBatchEntryByIds = makeGetBatchEntryByIds();

  return createSelector(getBatchEntryByIds, (batchEntries): boolean =>
    batchEntries && batchEntries.length > 0
      ? batchEntries
          .map((b) =>
            b
              ? !b.shipmentObjectId && b.objectStatus === BatchEntryStatus.PROCESSING_SHIPMENT
              : false,
          )
          .some((b) => b)
      : false,
  );
};

export const makeGetBatchEntriesIsCompleted = () => {
  const getBatchEntryByIds = makeGetBatchEntryByIds();

  return createSelector(getBatchEntryByIds, (batchEntries): boolean =>
    batchEntries && batchEntries.length > 0
      ? batchEntries
          .map((b) => (b ? b.objectStatus === BatchEntryStatus.COMPLETE_TRANSACTION : false))
          .some((b) => b)
      : false,
  );
};

export const getBatchEntryHasTransactionRequestFailed = createSelector(
  getBatchEntryById,
  (batchEntry): boolean =>
    batchEntry ? batchEntry.objectStatus === BatchEntryStatus.TRANSACTION_REQUEST_FAILED : false,
);

export const getOrderObjectIdByBatchEntry = createSelector(
  getBatchEntryStore,
  getBatchEntryIdFromProps,
  (batchEntryStore, batchEntryId): Maybe<string> => {
    return batchEntryStore.data[batchEntryId]?.orderObjectId || null;
  },
);

export const makeGetDoesBatchEntryNeedNewRates = () => {
  const getBatchEntryById = makeGetBatchEntryById();

  return createSelector(getBatchEntryById, (batchEntry): boolean => {
    if (!batchEntry) return false;

    return requiresNewRates(batchEntry);
  });
};

export const makeGetIsBatchEntryRepurchaseable = () => {
  const getBatchEntryIsCompleted = makeGetBatchEntryIsCompleted();

  return createSelector(
    getBatchEntryIsCompleted,
    getBatchEntryHasTransactionRequestFailed,
    (isCompleted, hasError) => isCompleted || hasError,
  );
};

export const getRuleForBatchEntry = createSelector(
  getBatchEntryAssociatedRules,
  getBatchEntryIdFromProps,
  (rules, batchEntryId): Maybe<BusinessRule[]> => {
    return (rules && rules[batchEntryId]) || null;
  },
);

export const getIsAnyBatchEntryASplitOrder = createSelector(
  getBatchEntryIdsFromProps,
  getBatchEntryData,
  (batchEntryIds, batchEntryData): boolean => {
    return batchEntryIds.some((batchEntryId) => {
      if (!batchEntryId || !batchEntryData || !batchEntryData[batchEntryId]) return false;

      const { partialFulfillmentRank } = batchEntryData[batchEntryId];

      return !!partialFulfillmentRank && partialFulfillmentRank > 0;
    });
  },
);

// This is a selector to make the transition from order being the primary focus, to batch entry.
// Should be deprecated after that work is finished
export const getFirstBatchEntryIdFromOrder = createSelector(
  getBatchEntriesByOrderId,
  getOrderObjectIdFromProps,
  (batchEntriesByOrder, orderObjectId): Maybe<number> => {
    const batchEntries = batchEntriesByOrder[orderObjectId] || [];
    if (batchEntries.length !== 1) return null;

    return batchEntries[0];
  },
);

// This is a selector to make the transition from order being the primary focus, to batch entry.
// Should be deprecated after that work is finished
export const getFirstBatchEntryFromOrder = createSelector(
  getFirstBatchEntryIdFromOrder,
  getBatchEntryData,
  (batchEntryId, batchEntryData): Maybe<BatchEntryExtended> => {
    if (!batchEntryId) return null;

    return batchEntryData[batchEntryId];
  },
);
